import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faSave } from '@fortawesome/free-solid-svg-icons';

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (noteText: string, images: File[]) => Promise<void>;
  customerName?: string;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  customerName,
}) => {
  const [noteText, setNoteText] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Reset when modal closes/opens
  useEffect(() => {
    if (isOpen) {
      setNoteText('');
      setImages([]);
      setImagePreview([]);
      setIsSaving(false);
    } else {
      // Cleanup previews
      imagePreview.forEach(url => URL.revokeObjectURL(url));
    }
  }, [isOpen]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length > 0) {
      setImages(prev => [...prev, ...validFiles]);
      
      // Create preview URLs
      const newPreviews = validFiles.map(file => URL.createObjectURL(file));
      setImagePreview(prev => [...prev, ...newPreviews]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    
    // Revoke URL to prevent memory leaks
    URL.revokeObjectURL(imagePreview[index]);
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!noteText.trim()) {
      return;
    }

    setIsSaving(true);
    
    try {
      await onSave(noteText, images);
      onClose();
    } catch (error) {
      console.error('Chyba při ukládání poznámky:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Nová poznámka</h3>
              {customerName && (
                <p className="text-sm text-gray-600 mt-1">Pro zákazníka: {customerName}</p>
              )}
            </div>
            <button 
              onClick={onClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Text poznámky *</label>
            <textarea
              rows={5}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              placeholder="Zadejte poznámku k zákazníkovi (preference, alergie, zvláštní požadavky...)"
              disabled={isSaving}
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Obrázky (volitelné)</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="note-image-upload"
                disabled={isSaving}
              />
              <label htmlFor="note-image-upload" className="cursor-pointer">
                <FontAwesomeIcon icon={faUpload} className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Klikněte pro nahrání obrázků nebo přetáhněte sem
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, GIF, WEBP do 10MB
                </p>
              </label>
            </div>

            {/* Image Previews */}
            {imagePreview.length > 0 && (
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {imagePreview.map((preview, index) => (
                    <div key={index} className="relative group">
                      <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => removeImage(index)}
                        disabled={isSaving}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-all shadow-lg disabled:opacity-50"
                        title="Odstranit obrázek"
                      >
                        <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-lg flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !noteText.trim()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {images.length > 0 ? 'Ukládání a nahrávání obrázků...' : 'Ukládání...'}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                Uložit poznámku
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNoteModal;

